import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FiChevronDown, FiChevronLeft, FiChevronRight, FiChevronUp } from 'react-icons/fi';
import { apiJson } from '@/lib/api';
import { track } from '@/lib/analytics';
import { formatPacificCivilDateLong } from '@/lib/adminPacificTime';
import type {
  AdminNarrativesDayResponse,
  HouseholdNarrative,
  NarrativeCounts,
  NarrativeHighlight,
} from '@/lib/types';

const PACIFIC_TZ = 'America/Los_Angeles';

const ADMIN_USER_LINK_CLASS =
  'inline-flex px-2 py-1 rounded-md border border-gray-300 text-xs font-semibold text-gray-700 hover:bg-gray-100';

const HIGHLIGHT_CHIP: Record<string, string> = {
  message: 'bg-sky-100 text-sky-900',
  expense: 'bg-amber-100 text-amber-950',
  payment: 'bg-emerald-100 text-emerald-900',
  journal: 'bg-teal-100 text-teal-900',
  check_in: 'bg-cyan-100 text-cyan-900',
  calendar: 'bg-indigo-100 text-indigo-900',
  exchange: 'bg-violet-100 text-violet-900',
  invite: 'bg-pink-100 text-pink-900',
  professional_invite: 'bg-fuchsia-100 text-fuchsia-900',
  vault: 'bg-slate-200 text-slate-900',
  dispute: 'bg-orange-100 text-orange-900',
  custody: 'bg-rose-100 text-rose-900',
};

const COUNT_CHIPS: { key: keyof NarrativeCounts; label: string }[] = [
  { key: 'messages', label: 'messages' },
  { key: 'expenses', label: 'expenses' },
  { key: 'payments', label: 'payments' },
  { key: 'journal_entries', label: 'journal' },
  { key: 'check_ins', label: 'check-ins' },
  { key: 'calendar_events', label: 'events' },
  { key: 'exchange_logs', label: 'exchanges' },
  { key: 'invites', label: 'invites' },
  { key: 'professional_invites', label: 'pro invites' },
  { key: 'vault_files', label: 'vault' },
  { key: 'disputes', label: 'disputes' },
  { key: 'custody_exceptions', label: 'custody' },
];

const SUMMARY_CARDS: { key: keyof AdminNarrativesDayResponse['summary']; label: string }[] = [
  { key: 'active_households', label: 'Active households' },
  { key: 'households_created', label: 'New households' },
  { key: 'messages', label: 'Messages' },
  { key: 'expenses', label: 'Expenses' },
  { key: 'payments', label: 'Payments' },
  { key: 'journal_entries', label: 'Journal' },
  { key: 'check_ins', label: 'Check-ins' },
  { key: 'invites', label: 'Invites' },
];

function parseYmd(s: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const [y, m, d] = s.split('-').map(Number);
  const u = new Date(Date.UTC(y, m - 1, d));
  if (u.getUTCFullYear() !== y || u.getUTCMonth() !== m - 1 || u.getUTCDate() !== d) return null;
  return s;
}

function addDaysYmd(ymd: string, delta: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const u = new Date(Date.UTC(y, m - 1, d + delta));
  return `${u.getUTCFullYear()}-${String(u.getUTCMonth() + 1).padStart(2, '0')}-${String(u.getUTCDate()).padStart(2, '0')}`;
}

function todayPacificYmd(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: PACIFIC_TZ });
}

function highlightLabel(kind: string): string {
  return kind.replace(/_/g, ' ');
}

function highlightTime(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString(undefined, {
    timeZone: PACIFIC_TZ,
    hour: 'numeric',
    minute: '2-digit',
  });
}

function childLabel(child: HouseholdNarrative['children'][number]): string {
  if (child.age_years == null) return child.name;
  if (child.age_years === 0) return `${child.name} (infant)`;
  return `${child.name} (${child.age_years})`;
}

function matchesQuery(row: HouseholdNarrative, q: string): boolean {
  if (!q) return true;
  const hay = [
    String(row.household_id),
    row.title,
    row.narrative,
    ...row.members.map((m) => [m.first_name, m.last_name, m.email, String(m.user_id)].filter(Boolean).join(' ')),
    ...row.children.map((c) => c.name),
  ]
    .join(' ')
    .toLowerCase();
  return hay.includes(q);
}

function HouseholdCard({
  row,
  expanded,
  onToggle,
}: {
  row: HouseholdNarrative;
  expanded: boolean;
  onToggle: () => void;
}) {
  const primaryUserId = row.primary_user_id ?? row.members[0]?.user_id;
  return (
    <article className='bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden'>
      <div className='px-4 py-3 border-b border-gray-100 flex flex-wrap items-start justify-between gap-3'>
        <div className='min-w-0'>
          <div className='flex flex-wrap items-center gap-2'>
            <h2 className='text-lg font-semibold text-gray-900'>{row.title}</h2>
            <span className='text-xs font-mono text-gray-500'>#{row.household_id}</span>
            {row.created_today ? (
              <span className='inline-flex px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 text-[11px] font-semibold'>
                New today
              </span>
            ) : null}
          </div>
          {row.children.length > 0 ? (
            <p className='text-sm text-gray-600 mt-0.5'>{row.children.map(childLabel).join(' · ')}</p>
          ) : null}
          <div className='flex flex-wrap gap-1.5 mt-2'>
            {row.members.map((m) => (
              <Link
                key={m.user_id}
                to='/admin/users/$userId'
                params={{ userId: String(m.user_id) }}
                search={{ hid: String(row.household_id) }}
                className='inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-700 hover:bg-gray-200'
              >
                {[m.first_name, m.last_name].filter(Boolean).join(' ') || `User #${m.user_id}`}
                {m.subscribed ? (
                  <span className='text-emerald-700'>· sub</span>
                ) : null}
              </Link>
            ))}
          </div>
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          {primaryUserId != null ? (
            <>
              <Link
                to='/admin/users/$userId/chat'
                params={{ userId: String(primaryUserId) }}
                search={{ hid: String(row.household_id) }}
                className={ADMIN_USER_LINK_CLASS}
              >
                Chat
              </Link>
              <Link
                to='/admin/users/$userId/households'
                params={{ userId: String(primaryUserId) }}
                search={{ hid: String(row.household_id) }}
                className={ADMIN_USER_LINK_CLASS}
              >
                Household
              </Link>
            </>
          ) : null}
        </div>
      </div>

      <div className='px-4 py-4'>
        <p className='text-[15px] leading-7 text-gray-800'>{row.narrative}</p>
        <div className='flex flex-wrap gap-1.5 mt-3'>
          {COUNT_CHIPS.filter((chip) => row.counts[chip.key] > 0).map((chip) => (
            <span
              key={chip.key}
              className='inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-700 tabular-nums'
            >
              {row.counts[chip.key]} {chip.label}
            </span>
          ))}
        </div>
      </div>

      {row.highlights.length > 0 ? (
        <div className='border-t border-gray-100'>
          <button
            type='button'
            onClick={onToggle}
            className='w-full flex items-center justify-between px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 hover:bg-gray-50'
          >
            <span>What happened</span>
            {expanded ? <FiChevronUp /> : <FiChevronDown />}
          </button>
          {expanded ? (
            <ol className='px-4 pb-4 space-y-2'>
              {row.highlights.map((item, idx) => (
                <HighlightRow key={`${item.kind}-${idx}-${item.created_at}`} item={item} />
              ))}
            </ol>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

function HighlightRow({ item }: { item: NarrativeHighlight }) {
  return (
    <li className='flex gap-3 text-sm'>
      <span className='shrink-0 w-16 text-xs tabular-nums text-gray-400 pt-0.5'>
        {highlightTime(item.created_at)}
      </span>
      <span
        className={`shrink-0 inline-flex h-5 items-center rounded-full px-2 text-[10px] font-semibold capitalize ${HIGHLIGHT_CHIP[item.kind] ?? 'bg-slate-100 text-slate-800'}`}
      >
        {highlightLabel(item.kind)}
      </span>
      <div className='min-w-0'>
        {item.actor_name ? (
          <span className='font-medium text-gray-800'>{item.actor_name} </span>
        ) : null}
        <span className='text-gray-700'>{item.text}</span>
      </div>
    </li>
  );
}

function AdminNarrativesPage() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const { date: dateParam } = Route.useSearch();
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState<Set<number>>(() => new Set());

  const dateStr = useMemo(() => {
    const parsed = dateParam ? parseYmd(dateParam) : null;
    return parsed ?? todayPacificYmd();
  }, [dateParam]);

  useEffect(() => {
    track('Narratives Screen', { date: dateStr });
  }, [dateStr]);

  const narrativesQuery = useQuery({
    queryKey: ['admin', 'narratives', dateStr],
    queryFn: () =>
      apiJson<AdminNarrativesDayResponse>(
        getToken,
        `/v1/admin/narratives?date=${encodeURIComponent(dateStr)}`,
      ),
  });

  const setDate = useCallback(
    (next: string) => {
      navigate({ to: '/admin/narratives', search: { date: next } });
    },
    [navigate],
  );

  const data = narrativesQuery.data;
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data?.households ?? []).filter((row) => matchesQuery(row, q));
  }, [data?.households, query]);

  const toggle = useCallback((householdId: number) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(householdId)) next.delete(householdId);
      else next.add(householdId);
      return next;
    });
  }, []);

  return (
    <div className='space-y-4 sm:space-y-6'>
      <div className='flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between'>
        <div className='min-w-0'>
          <h1 className='text-xl font-semibold text-gray-900 sm:text-2xl'>Narratives</h1>
          <p className='text-sm text-gray-500 mt-1'>
            A readable day-in-the-life of each active household. One Pacific calendar day at a
            time.
          </p>
          <p className='text-sm text-gray-700 mt-1 font-medium'>
            {formatPacificCivilDateLong(dateStr)}
          </p>
        </div>
        <div className='flex w-full flex-col gap-2 sm:w-auto sm:shrink-0'>
          <div className='flex items-center gap-2'>
            <button
              type='button'
              onClick={() => setDate(addDaysYmd(dateStr, -1))}
              className='shrink-0 p-2.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50'
              aria-label='Previous day'
            >
              <FiChevronLeft className='w-5 h-5' />
            </button>
            <input
              type='date'
              value={dateStr}
              onChange={(e) => {
                const v = e.target.value;
                if (v) setDate(v);
              }}
              className='min-w-0 flex-1 px-3 py-2.5 border border-gray-300 rounded-md text-sm font-medium text-gray-900'
            />
            <button
              type='button'
              onClick={() => setDate(addDaysYmd(dateStr, 1))}
              className='shrink-0 p-2.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50'
              aria-label='Next day'
            >
              <FiChevronRight className='w-5 h-5' />
            </button>
          </div>
          <button
            type='button'
            onClick={() => setDate(todayPacificYmd())}
            title='Jump to today (US Pacific)'
            className='w-full px-3 py-2.5 text-sm font-semibold rounded-md border border-primary text-primary hover:bg-primary/5 sm:w-auto'
          >
            Today (Pacific)
          </button>
        </div>
      </div>

      {narrativesQuery.isLoading ? (
        <div className='flex justify-center py-16'>
          <div className='animate-spin h-10 w-10 border-2 border-primary border-t-transparent rounded-full' />
        </div>
      ) : narrativesQuery.isError ? (
        <p className='text-red-600'>Could not load narratives for this day.</p>
      ) : !data ? null : (
        <>
          <div className='-mx-6 px-6 sm:mx-0 sm:px-0'>
            <div className='flex gap-2 overflow-x-auto pb-1 snap-x snap-mandatory sm:grid sm:grid-cols-4 sm:overflow-visible lg:grid-cols-8'>
              {SUMMARY_CARDS.map(({ key, label }) => (
                <div
                  key={key}
                  className='min-w-[7.25rem] shrink-0 snap-start bg-white rounded-lg border border-gray-200 px-3 py-3 shadow-sm sm:min-w-0'
                >
                  <p className='text-[11px] font-semibold uppercase tracking-wide text-gray-500 leading-tight'>
                    {label}
                  </p>
                  <p className='mt-1 text-xl font-semibold tabular-nums text-gray-900'>
                    {data.summary[key].toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className='bg-white rounded-lg border border-gray-200 p-3'>
            <label className='block text-xs font-semibold text-gray-600 mb-1'>
              Filter households
            </label>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder='Name, email, household ID, or a phrase from the story'
              className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
            />
          </div>

          {filtered.length === 0 ? (
            <p className='text-sm text-gray-500 py-10 text-center'>
              {data.households.length === 0
                ? 'No households were active this Pacific day.'
                : 'No households match that filter.'}
            </p>
          ) : (
            <div className='space-y-4'>
              {filtered.map((row) => (
                <HouseholdCard
                  key={row.household_id}
                  row={row}
                  expanded={!collapsed.has(row.household_id)}
                  onToggle={() => toggle(row.household_id)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export const Route = createFileRoute('/admin/narratives')({
  component: AdminNarrativesPage,
  validateSearch: (search: Record<string, unknown>) => ({
    date: typeof search.date === 'string' ? search.date : undefined,
  }),
});
